// Supabase client for RentMzansi
import { createClient } from '@supabase/supabase-js';

// Use environment variables for security (with fallback for development)
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://xohczphzosfgdlgwrlrw.supabase.co';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InhvaGN6cGh6b3NmZ2RsZ3dybHJ3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjQ2MTE1NDQsImV4cCI6MjA4MDE4NzU0NH0.AlW0ul9nwusOjEM36ERhN5XYsj0FzDHBBjufwd0rRYM';

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// ===========================
// AUTHENTICATION FUNCTIONS
// ===========================

// Sign up with email and password
// Returns { user, pendingConfirmation } - pendingConfirmation is true when
// a brand-new account was created and is waiting on the user to click the
// confirmation link emailed to them (session will be null until then).
export async function signUpWithEmail(email, password, captchaToken) {
  const payload = {
    email,
    password,
    options: {
      emailRedirectTo: window.location.origin,
    }
  };

  if (captchaToken) {
    payload.options.captchaToken = captchaToken;
  }

  const { data, error } = await supabase.auth.signUp(payload);
  
  if (error) {
    console.error('Sign up error:', error);
    throw error;
  }
  
  // Supabase returns a user object with an EMPTY identities array when the
  // email already belongs to an existing account. No confirmation email is
  // sent in this case (this obfuscation is intentional, to stop attackers
  // from using signup to discover which emails already have accounts) - so
  // we must NOT tell the user to "check their email" here, since none will
  // ever arrive. Surface a clear, actionable message instead.
  if (data.user && data.user.identities && data.user.identities.length === 0) {
    const err = new Error('This email is already registered. Please sign in instead, or use "Forgot password" if you need to reset it.');
    err.code = 'auth/email-already-in-use';
    throw err;
  }
  
  // Session exists immediately - email confirmation is disabled in Supabase settings
  if (data.session) {
    return { user: data.user, pendingConfirmation: false };
  }
  
  // Brand-new account created, confirmation email has been sent
  if (data.user && !data.session) {
    return { user: data.user, pendingConfirmation: true };
  }
  
  return { user: data.user, pendingConfirmation: false };
}

// Resend the signup confirmation email (used when a user's first email
// never arrived, or they signed in before confirming and got stuck).
export async function resendConfirmationEmail(email, captchaToken = '') {
  const options = {
    emailRedirectTo: window.location.origin,
  };

  if (captchaToken) {
    options.captchaToken = captchaToken;
  }

  const { error } = await supabase.auth.resend({
    type: 'signup',
    email,
    options,
  });

  if (error) {
    console.error('Resend confirmation error:', error);
    throw error;
  }
}

// Sign in with email and password
export async function signInWithEmail(email, password, captchaToken) {
  const payload = {
    email,
    password,
  };

  if (captchaToken) {
    payload.options = { captchaToken };
  }

  const { data, error } = await supabase.auth.signInWithPassword(payload);
  
  if (error) {
    console.error('Sign in error:', error);
    throw error;
  }
  
  return data.user;
}

// Sign in with Google
export async function signInWithGoogle(userType = 'renter') {
  // The OAuth flow navigates away and back, so React state (and the user's
  // renter/landlord choice) would otherwise be lost. Stash it in localStorage
  // so App.js can pick it up once the session comes back after redirect.
  try {
    localStorage.setItem('pending-oauth-user-type', userType === 'landlord' ? 'landlord' : 'renter');
  } catch (e) {
    // localStorage may be unavailable (e.g. private browsing) - non-fatal
  }

  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: window.location.origin,
    }
  });
  
  if (error) {
    console.error('Google sign in error:', error);
    throw error;
  }
  
  return data;
}

// Sign out
export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) {
    console.error('Sign out error:', error);
    throw error;
  }
}

// Get current user
export function getCurrentUser() {
  return supabase.auth.getUser();
}

// Get current session
export function getSession() {
  return supabase.auth.getSession();
}

// Listen to auth state changes
export function onAuthStateChange(callback) {
  return supabase.auth.onAuthStateChange((event, session) => {
    callback(session?.user || null, event);
  });
}

// Send password reset email
export async function resetPassword(email, captchaToken = '') {
  const options = {
    redirectTo: `${window.location.origin}/reset-password`,
  };
  
  if (captchaToken) {
    options.captchaToken = captchaToken;
  }
  
  const { error } = await supabase.auth.resetPasswordForEmail(email, options);
  
  if (error) {
    console.error('Password reset error:', error);
    throw error;
  }
}

// ===========================
// PROFILE FUNCTIONS (localStorage-based for simplicity)
// ===========================

export function getProfile(userId) {
  const profile = localStorage.getItem(`user-profile-${userId}`);
  return profile ? JSON.parse(profile) : null;
}

export function saveProfile(userId, profileData) {
  localStorage.setItem(`user-profile-${userId}`, JSON.stringify(profileData));
  localStorage.setItem(`user-type-${userId}`, profileData.userType || 'renter');
  if (profileData.landlordComplete) {
    localStorage.setItem(`landlord-complete-${userId}`, 'true');
  }
  return profileData;
}

export function updateProfile(userId, updates) {
  const existing = getProfile(userId) || {};
  const updated = { ...existing, ...updates };
  saveProfile(userId, updated);
  return updated;
}

export function isLandlordComplete(userId) {
  return localStorage.getItem(`landlord-complete-${userId}`) === 'true';
}

export function setLandlordComplete(userId, complete = true) {
  localStorage.setItem(`landlord-complete-${userId}`, complete ? 'true' : 'false');
}

export function getUserType(userId) {
  return localStorage.getItem(`user-type-${userId}`) || 'renter';
}

export async function ensureProfileRecord(userId) {
  if (!userId) {
    return null;
  }

  try {
    const { data: existing, error: fetchError } = await supabase
      .from('profiles')
      .select('id')
      .eq('id', userId)
      .maybeSingle();

    if (fetchError) {
      console.error('Error checking profile record:', fetchError);
      throw fetchError;
    }

    if (existing) {
      return existing;
    }

    const { data, error } = await supabase
      .from('profiles')
      .insert({ id: userId })
      .select('id')
      .single();

    if (error) {
      console.error('Error creating profile record:', error);
      throw error;
    }

    return data;
  } catch (err) {
    console.error('ensureProfileRecord failed:', err);
    throw err;
  }
}

// Helper to sanitize phone numbers for database constraint
function sanitizePhone(phone) {
  if (!phone) return null; // Use NULL instead of empty string
  // Remove spaces, dashes, parentheses, dots
  const cleaned = phone.replace(/[\s\-().]/g, '');
  // Only return if it looks like a valid phone (7-15 digits, optional + prefix)
  if (/^[+]?[0-9]{7,15}$/.test(cleaned)) {
    return cleaned;
  }
  return null; // Invalid format, store as NULL
}

// Helper to validate email format
function sanitizeEmail(email) {
  if (!email || !email.trim()) return null;
  const trimmed = email.trim().toLowerCase();
  // Basic email validation
  if (/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(trimmed)) {
    return trimmed;
  }
  return null;
}

export async function syncProfileToSupabase(userId, profileData = {}) {
  if (!userId) return false;

  const payload = {
    display_name: (profileData.displayName || profileData.name || '').trim() || null,
    email: sanitizeEmail(profileData.email),
    phone: sanitizePhone(profileData.phone),
    whatsapp: profileData.whatsapp?.trim() || null,
    user_type: profileData.userType || 'renter',
    photo_url: profileData.photoURL || profileData.photo || null,
    landlord_complete: !!profileData.landlordComplete,
    landlord_info: profileData.landlordInfo || null,
    notification_prefs: profileData.notificationPrefs || null,
    updated_at: new Date().toISOString(),
  };

  try {
    // First, check if profile exists
    const { data: existing, error: fetchError } = await supabase
      .from('profiles')
      .select('id')
      .eq('id', userId)
      .maybeSingle();

    if (fetchError) {
      console.error('Error checking profile:', fetchError);
      return false; // Don't throw, just return false
    }

    if (existing) {
      // Update existing profile
      const { error: updateError } = await supabase
        .from('profiles')
        .update(payload)
        .eq('id', userId);

      if (updateError) {
        console.error('syncProfileToSupabase update failed:', updateError.message);
        return false;
      }
    } else {
      // Insert new profile
      const { error: insertError } = await supabase
        .from('profiles')
        .insert({ id: userId, ...payload });

      if (insertError) {
        console.error('syncProfileToSupabase insert failed:', insertError.message);
        return false;
      }
    }

    return true;
  } catch (err) {
    console.error('syncProfileToSupabase error:', err);
    return false;
  }
}

// ===========================
// LISTINGS FUNCTIONS
// ===========================

export async function fetchAllListings() {
  const { data, error } = await supabase
    .from('listings')
    .select('*')
    .order('created_at', { ascending: false });
  
  if (error) {
    console.error('Error fetching listings:', error);
    return [];
  }
  
  // Convert snake_case to camelCase for frontend
  return data.map(listing => ({
    id: listing.id,
    title: listing.title,
    price: listing.price,
    location: listing.location,
    streetAddress: listing.street_address,
    fullAddress: listing.full_address,
    description: listing.description,
    photos: listing.photos || [],
    status: listing.status,
    availableDate: listing.available_date,
    amenities: listing.amenities || [],
    latitude: listing.latitude,
    longitude: listing.longitude,
    paymentMethod: listing.payment_method,
    premium: listing.premium,
    landlordId: listing.landlord_id,
    landlordName: listing.landlord_name,
    landlordPhone: listing.landlord_phone,
    landlordEmail: listing.landlord_email,
    landlordPhoto: listing.landlord_photo,
    createdAt: listing.created_at,
    listingType: listing.listing_type || 'room',
    // Guesthouse/BnB specific fields
    priceType: listing.price_type || 'monthly',
    minStay: listing.min_stay || 1,
    maxStay: listing.max_stay || 0,
    checkInTime: listing.check_in_time || '14:00',
    checkOutTime: listing.check_out_time || '10:00',
    breakfastIncluded: listing.breakfast_included || false,
    cleaningFee: listing.cleaning_fee || '',
    houseRules: listing.house_rules || '',
  }));
}

export async function createListing(listingData) {
  if (!listingData?.landlordId) {
    throw new Error('Missing landlordId for listing creation');
  }

  // Ensure the foreign-key target exists so inserts don't fail
  await ensureProfileRecord(listingData.landlordId);

  const { data, error } = await supabase
    .from('listings')
    .insert({
      title: listingData.title,
      price: listingData.price,
      location: listingData.location,
      street_address: listingData.streetAddress,
      full_address: listingData.fullAddress,
      description: listingData.description,
      photos: listingData.photos || [],
      status: listingData.status || 'available',
      available_date: listingData.availableDate,
      amenities: listingData.amenities || [],
      latitude: listingData.latitude,
      longitude: listingData.longitude,
      payment_method: listingData.paymentMethod,
      premium: listingData.premium || false,
      landlord_id: listingData.landlordId,
      landlord_name: listingData.landlordName,
      landlord_phone: listingData.landlordPhone,
      landlord_email: listingData.landlordEmail,
      landlord_photo: listingData.landlordPhoto,
      listing_type: listingData.listingType || 'room',
      // Guesthouse/BnB specific fields
      price_type: listingData.priceType || 'monthly',
      min_stay: listingData.minStay || 1,
      max_stay: listingData.maxStay || 0,
      check_in_time: listingData.checkInTime || '14:00',
      check_out_time: listingData.checkOutTime || '10:00',
      breakfast_included: listingData.breakfastIncluded || false,
      cleaning_fee: listingData.cleaningFee || null,
      house_rules: listingData.houseRules || null,
    })
    .select()
    .single();
  
  if (error) {
    console.error('Error creating listing:', error);
    throw error;
  }
  
  return {
    id: data.id,
    title: data.title,
    price: data.price,
    location: data.location,
    streetAddress: data.street_address,
    fullAddress: data.full_address,
    description: data.description,
    photos: data.photos || [],
    status: data.status,
    availableDate: data.available_date,
    amenities: data.amenities || [],
    latitude: data.latitude,
    longitude: data.longitude,
    paymentMethod: data.payment_method,
    premium: data.premium,
    landlordId: data.landlord_id,
    landlordName: data.landlord_name,
    landlordPhone: data.landlord_phone,
    landlordEmail: data.landlord_email,
    landlordPhoto: data.landlord_photo,
    createdAt: data.created_at,
  };
}

export async function deleteListing(listingId) {
  const { error } = await supabase
    .from('listings')
    .delete()
    .eq('id', listingId);
  
  if (error) {
    console.error('Error deleting listing:', error);
    throw error;
  }
  return true;
}

export async function updateListing(listingId, updates) {
  const dbUpdates = {};
  if (updates.title !== undefined) dbUpdates.title = updates.title;
  if (updates.price !== undefined) dbUpdates.price = updates.price;
  if (updates.location !== undefined) dbUpdates.location = updates.location;
  if (updates.streetAddress !== undefined) dbUpdates.street_address = updates.streetAddress;
  if (updates.fullAddress !== undefined) dbUpdates.full_address = updates.fullAddress;
  if (updates.description !== undefined) dbUpdates.description = updates.description;
  if (updates.photos !== undefined) dbUpdates.photos = updates.photos;
  if (updates.status !== undefined) dbUpdates.status = updates.status;
  if (updates.availableDate !== undefined) dbUpdates.available_date = updates.availableDate;
  if (updates.amenities !== undefined) dbUpdates.amenities = updates.amenities;
  if (updates.listingType !== undefined) dbUpdates.listing_type = updates.listingType;
  // Guesthouse/BnB specific fields
  if (updates.priceType !== undefined) dbUpdates.price_type = updates.priceType;
  if (updates.minStay !== undefined) dbUpdates.min_stay = updates.minStay;
  if (updates.maxStay !== undefined) dbUpdates.max_stay = updates.maxStay;
  if (updates.checkInTime !== undefined) dbUpdates.check_in_time = updates.checkInTime;
  if (updates.checkOutTime !== undefined) dbUpdates.check_out_time = updates.checkOutTime;
  if (updates.breakfastIncluded !== undefined) dbUpdates.breakfast_included = updates.breakfastIncluded;
  if (updates.cleaningFee !== undefined) dbUpdates.cleaning_fee = updates.cleaningFee;
  if (updates.houseRules !== undefined) dbUpdates.house_rules = updates.houseRules;

  const { data, error } = await supabase
    .from('listings')
    .update(dbUpdates)
    .eq('id', listingId)
    .select()
    .single();
  
  if (error) {
    console.error('Error updating listing:', error);
    throw error;
  }
  return data;
}

// Subscribe to real-time listing changes
export function subscribeToListings(callback) {
  const subscription = supabase
    .channel('listings-channel')
    .on('postgres_changes', 
      { event: '*', schema: 'public', table: 'listings' },
      async () => {
        // Refetch all listings when any change occurs
        const listings = await fetchAllListings();
        callback(listings);
      }
    )
    .subscribe();
  
  return () => {
    subscription.unsubscribe();
  };
}
